import { Avatar, AvatarFallback } from '@saay/shadcn/components/avatar';
import { cn } from '@saay/shadcn/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@saay/shadcn/components/sidebar';
import { BrandPsi } from '@saay/ui';
import { ChevronLeftIcon, type LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { useTopBarVisibility } from './buyer.js';

export interface PanelNavGroup {
  readonly group?: string;
  readonly items: readonly {
    readonly label: string;
    readonly icon: LucideIcon;
    readonly active?: boolean;
    /** A count badge (decidable approvals, pending sales). */
    readonly badge?: string;
    /** A router link (e.g. `<NavLink to="/sales" />`). */
    readonly render?: ReactElement;
  }[];
}

/**
 * The desktop panel frame of the merchant and admin apps (D-93): the shadcn Sidebar on the right
 * (RTL start) with the organisation card, grouped items (the active one takes the olive
 * `--sidebar-accent` token), and the user card that opens «حساب من»; then the page.
 */
export function PanelShell({
  org,
  orgHint,
  nav,
  user,
  role,
  account,
  topBar,
  mobileNav,
  children,
}: {
  readonly org: string;
  readonly orgHint: string;
  readonly nav: readonly PanelNavGroup[];
  readonly user: string;
  readonly role: string;
  /** The «حساب من» link for the user card. */
  readonly account?: ReactElement;
  readonly topBar: ReactNode;
  /** The phone navigation (the app's bottom bar with its raised centre action), shown below `md`. */
  readonly mobileNav?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <SidebarProvider className="min-h-dvh">
      <Sidebar
        className="sticky top-0 hidden h-dvh w-64 border-e md:flex"
        collapsible="none"
        side="right"
      >
        <SidebarHeader className="p-4">
          <div className="flex items-center gap-3 rounded-2xl bg-muted p-2.5">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand p-2 text-brand-gold">
              <BrandPsi className="size-full" />
            </span>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-sm font-bold">{org}</p>
              <p className="truncate text-xs text-muted-foreground">{orgHint}</p>
            </div>
          </div>
        </SidebarHeader>
        <SidebarContent className="px-2">
          {nav.map((group, index) => (
            <SidebarGroup className="py-0.5" key={group.group ?? index}>
              {group.group === undefined ? null : (
                <SidebarGroupLabel>{group.group}</SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className="gap-0.5">
                  {group.items.map((item) => (
                    <SidebarMenuItem key={item.label}>
                      <SidebarMenuButton
                        className="h-9 rounded-xl px-3 text-muted-foreground hover:text-foreground data-active:font-bold [&_svg]:size-[18px]"
                        isActive={item.active === true}
                        {...(item.render === undefined ? {} : { render: item.render })}
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                      {item.badge === undefined ? null : (
                        <SidebarMenuBadge className="top-2! end-2 rounded-full bg-tone-danger-strong px-1.5 text-[11px] text-white peer-data-active/menu-button:text-white">
                          {item.badge}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>
        <SidebarFooter className="p-4">
          <SidebarMenuButton
            aria-label="حساب من"
            className="h-auto gap-3 rounded-2xl p-2"
            {...(account === undefined ? {} : { render: account })}
          >
            <Avatar className="size-9">
              <AvatarFallback className="bg-tone-gold text-sm font-bold text-tone-gold-foreground">
                {user.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-bold">{user}</p>
              <p className="truncate text-xs text-muted-foreground">{role}</p>
            </div>
            <ChevronLeftIcon className="ms-auto size-4 text-muted-foreground" />
          </SidebarMenuButton>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset className="min-w-0 bg-background">
        {topBar}
        {/* SidebarInset is the page's <main>; the content wrapper is a plain block. */}
        <div
          className={`flex flex-col gap-4 px-4 pt-4 md:gap-6 md:px-8 ${mobileNav === undefined ? 'pb-10' : 'pb-28 md:pb-10'}`}
        >
          {children}
        </div>
        {mobileNav === undefined ? null : <div className="md:hidden">{mobileNav}</div>}
      </SidebarInset>
    </SidebarProvider>
  );
}

/** The panel's top bar: title with the date under it, then the page's controls (search, one action, bell). */
export function PanelTopBar({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children?: ReactNode;
}) {
  const hidden = useTopBarVisibility();
  return (
    // The same floating glass bar as the buyer apps (owner, 2026-10-19): rounded, a small gap from
    // the edges, content scrolling under it.
    <header
      className={cn(
        'sticky top-0 z-20 px-3 pt-3 transition-[transform,opacity] duration-[250ms] ease-out motion-reduce:transition-none md:px-6',
        hidden && '-translate-y-[calc(100%+0.75rem)] opacity-0',
      )}
      data-topbar-hidden={hidden ? '' : undefined}
    >
      <div
        className={cn(
          'glass mx-auto flex h-[60px] items-center gap-3 rounded-[20px] transition-[width] duration-[250ms] ease-out motion-reduce:transition-none px-4 md:h-16 md:gap-4 md:px-5',
          hidden ? 'w-4/5' : 'w-full',
        )}
      >
        <div className="min-w-0 leading-tight">
          <h1 className="truncate text-lg font-extrabold md:text-xl">{title}</h1>
          {subtitle === undefined ? null : (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div className="ms-auto flex items-center gap-2 md:gap-3">{children}</div>
      </div>
    </header>
  );
}
